import type { ReactNode } from 'react';
import { TextCursorInput } from 'lucide-react';
import type { JobMobileState } from './use-job-mobile';
import type { PaigeMobileBinding } from './mobile-types';
import { useJobFieldNames } from './use-job-field-names';
import { suggestedSenderField } from './mobile-words';

type Attribute = PaigeMobileBinding['attribute'];

const attributes: { id: Attribute; title: string }[] = [
  { id: 'sender_name', title: 'Who sent it' },
  { id: 'sender_email', title: 'Their email' },
  { id: 'captured_at', title: 'When it was scanned' },
  { id: 'phone', title: 'The phone' },
];

/**
 * Scan from a phone › Options › Fill fields from the phone (docs/engineering/mobile-capture.md, section
 * 7): each thing the phone knows picks one of the job's fields, by name, or none. The values are filled
 * for review, never as checked by a person, and never over a value a person already confirmed. The place
 * is never written into a field.
 */
export function MobileFields({ state }: { state: JobMobileState }): ReactNode {
  const mobile = state.mobile!;
  const names = useJobFieldNames();
  const bound = new Map(mobile.fields.map((binding) => [binding.attribute, binding.field]));
  const suggestion = bound.has('sender_name') ? null : suggestedSenderField(names);
  const runsJobs = window.paigeWorkbench?.runsJobs !== false;
  const set = (attribute: Attribute, field: string) => {
    const next = mobile.fields.filter((binding) => binding.attribute !== attribute);
    void state.save({ fields: field ? [...next, { attribute, field }] : next });
  };
  return (
    <div className="paige-phone-option paige-phone-fields">
      <FieldsHead
        suggestion={runsJobs ? suggestion : null}
        onSuggest={(field) => set('sender_name', field)}
      />
      <span className="paige-phone-field-grid">
        {attributes.map((attribute) => (
          <FieldChoice
            key={attribute.id}
            title={attribute.title}
            value={bound.get(attribute.id) ?? ''}
            names={names}
            disabled={state.saving || !runsJobs}
            onChange={(field) => set(attribute.id, field)}
          />
        ))}
      </span>
    </div>
  );
}

/** The row's mark and words, and the one-click suggestion when the job has a likely field. */
function FieldsHead({
  suggestion,
  onSuggest,
}: {
  suggestion: string | null;
  onSuggest: (field: string) => void;
}): ReactNode {
  return (
    <>
      <span className="paige-email-sheet-row-icon" aria-hidden="true">
        <TextCursorInput size={20} />
      </span>
      <span>
        <strong>Fill fields from the phone</strong>
        <small>
          Who sent it and when, written into this job’s fields.
          {suggestion && (
            <>
              {' '}
              <button
                type="button"
                className="paige-phone-suggest"
                onClick={() => onSuggest(suggestion)}
              >
                Fill {suggestion} with who sent it
              </button>
            </>
          )}
        </small>
      </span>
    </>
  );
}

function FieldChoice(props: {
  title: string;
  value: string;
  names: string[];
  disabled: boolean;
  onChange: (field: string) => void;
}): ReactNode {
  const { value } = props;
  const names = value && !props.names.includes(value) ? [value, ...props.names] : props.names;
  return (
    <label className="paige-mobile-field">
      <small>{props.title}</small>
      <select
        value={value}
        disabled={props.disabled}
        aria-label={`${props.title}: the field it fills`}
        onChange={(event) => props.onChange(event.currentTarget.value)}
      >
        <option value="">No field</option>
        {names.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}
